import clsx from 'clsx'
import compact from 'lodash/compact'
import toInteger from 'lodash/toInteger'
import { useRouter } from 'next/router'
import React, { createContext, FC, ReactNode, useCallback, useEffect, useMemo } from 'react'

import { NativeModal, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { PromptDialog } from '@Domains/Cart/Components/Prompt/Dialog'
import { useAddToCart } from '@Domains/Cart/Hooks/useAddToCart'
import { CartSummaryQuery, useCartSummaryQuery } from '@Generated/graphql'

import { SideCartDisplay } from '../Feature/SideCart/Components/Display/SideCartDisplay'
import styles from './SideCartContextProvider.module.scss'

interface SideCartProviderProps {
  children: ReactNode
}

interface SideCartContextProps {
  cartSummary?: NonNullable<CartSummaryQuery['cart']>
  loadingCart?: boolean
  openCart: () => void
}

export const SideCartContext = createContext<SideCartContextProps>({
  cartSummary: undefined,
  loadingCart: false,
  openCart: () => {
  },
})

export const SideCartProvider: FC<SideCartProviderProps> = ({ children }) => {
  const actionRef = useNativeModalActions()
  const router = useRouter()
  const [addToCart, { isLoading, prompts, reset, variables }] = useAddToCart({ useCache: false })
  const { data, loading } = useCartSummaryQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',
  })
  const openCart = useCallback(() => {
    actionRef.current?.open()
  }, [actionRef])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    if (window.location.search.includes('recover_cart_products')) {
      const urlParams = new URLSearchParams(window.location.search)
      const recoverCartProducts = urlParams.get('recover_cart_products')
      if (recoverCartProducts) {
        const products = compact(recoverCartProducts.split(',').map((item) => {
          const [productID, variantID, quantityStr] = item.split(':')
          const quantity = toInteger(quantityStr)

          if (!productID || !variantID || !quantity) {
            return null
          }

          return { productID, variantID, quantity, skipIfExists: true }
        }))

        if (products.length > 0) {
          addToCart(products, {
            sourceID: 'recover_cart_products',
            withoutSuccessPrompt: true,
          }).then((addToCartResult) => {
            if (addToCartResult?.data?.addToCart?.success || addToCartResult?.data?.addToCart?.prompts?.length === 0) {
              openCart()
            }
          }).catch(() => {
            openCart()
          })
        }
      }
    }

    if (router.query.cart === 'true' && actionRef.current) {
      openCart()
      window.localStorage.removeItem('triggerSideCart')
    }
    if (window.localStorage.getItem('triggerSideCart') && (router.pathname.endsWith('/success') || router.pathname.includes('/checkout/'))) {
      window.localStorage.removeItem('triggerSideCart')
    } else if (window.localStorage.getItem('triggerSideCart') && !router.pathname.endsWith('/onestepcheckout')) {
      openCart()
      window.localStorage.removeItem('triggerSideCart')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(
    () => ({ openCart, cartSummary: data?.cart || undefined, loadingCart: loading }),
    [data?.cart, openCart, loading],
  )

  return (
    <SideCartContext.Provider value={value}>
      {children}
      {prompts && prompts.length !== 0 && (
        <PromptDialog
          isLoading={isLoading}
          onClose={reset}
          onSubmit={(addToCartData) => {
            addToCart(addToCartData, {
              sourceID: 'recover_cart_products',
              withoutSuccessPrompt: true,
            }).then((addToCartResult) => {
              if (addToCartResult?.data?.addToCart?.success) {
                openCart()
              }
            })
          }}
          prompts={prompts}
          promptsOrigin={variables}
        />
      )}
      <NativeModal
        actionRef={actionRef}
        alignment="right"
        backdrop
        className={clsx(styles.sideCartContextProvider)}
      >
        <SideCartDisplay />
      </NativeModal>
    </SideCartContext.Provider>
  )
}
